// AGC: Firebase Authentication — auth providers + sign-in methods
// AGC: User profile CRUD extracted to userProfileService.ts
// AGC: Firebase init extracted to lib/firebase.ts

import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPhoneNumber,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  RecaptchaVerifier,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  User,
  UserCredential,
  ConfirmationResult,
} from 'firebase/auth'
import { Capacitor } from '@capacitor/core'
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth'
import { signInWithCredential, GoogleAuthProvider as FirebaseGoogleAuthProvider } from 'firebase/auth'

// AGC: Import auth, db, storage from centralized lib/firebase.ts
import { auth, db, storage } from '../lib/firebase'

// AGC: Re-export auth, db, storage, and all profile/team functions for backward compat
export { auth, db, storage }
export {
  getUserProfile,
  createUserProfile,
  updateUserProfile,
  updateLastLogin,
  updateBiometricStatus,
  getUsersByManager,
  getAllUsers,
  updateUserRole,
  deactivateUser,
  reactivateUser,
  getTeamMemberCount,
  createTeamMember,
  updateTeamMemberSubtype,
  type CreateTeamMemberData,
} from './userProfileService'

// ========================================
// AGC: AUTH PROVIDERS
// ========================================

const googleProvider = new GoogleAuthProvider()
googleProvider.addScope('email')
googleProvider.addScope('profile')

const appleProvider = new OAuthProvider('apple.com')
appleProvider.addScope('email')
appleProvider.addScope('name')

// ========================================
// AGC: SIGN IN METHODS
// ========================================

const isCapacitorApp = (): boolean => {
  try { return Capacitor.isNativePlatform() } catch { return false }
}

export const initializeGoogleAuth = async (): Promise<void> => {
  if (isCapacitorApp()) {
    try {
      await GoogleAuth.initialize({
        clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '187508989718-4s0a7a2ja5iaaadclub50v859upp8pom.apps.googleusercontent.com',
        scopes: ['profile', 'email'],
        grantOfflineAccess: true,
      })
    } catch (error) {
      console.error('❌ AGC: Failed to initialize Google Auth:', error)
    }
  }
}

export const signInWithGoogle = async (): Promise<UserCredential | null> => {
  if (isCapacitorApp()) {
    try {
      const googleUser = await GoogleAuth.signIn()
      const credential = FirebaseGoogleAuthProvider.credential(googleUser.authentication.idToken)
      const result = await signInWithCredential(auth, credential)
      // AGC: Sync Google profile photo to Firebase (Capacitor credential doesn't include it)
      const photoUrl = googleUser.user?.imageUrl || googleUser.user?.photoUrl
      if (result.user && photoUrl && !result.user.photoURL) {
        await updateProfile(result.user, { photoURL: photoUrl }).catch(() => {})
      }
      return result
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : ''
      if (msg.includes('cancel')) throw new Error('Login cancelado pelo usuário')
      throw error
    }
  }
  return signInWithPopup(auth, googleProvider)
}

export const signInWithApple = async (): Promise<UserCredential | null> => {
  if (isCapacitorApp()) {
    await signInWithRedirect(auth, appleProvider)
    return null as unknown as UserCredential
  }
  return signInWithPopup(auth, appleProvider)
}

export const handleRedirectResult = async (): Promise<UserCredential | null> => {
  try {
    return await getRedirectResult(auth)
  } catch (error) {
    console.error('❌ AGC: Redirect result error:', error)
    throw error
  }
}

export const signInWithEmail = async (email: string, password: string): Promise<UserCredential> => {
  return signInWithEmailAndPassword(auth, email, password)
}

export const createUserWithEmail = async (
  email: string, password: string, displayName?: string
): Promise<UserCredential> => {
  const credential = await createUserWithEmailAndPassword(auth, email, password)
  if (displayName && credential.user) {
    await updateProfile(credential.user, { displayName })
  }
  return credential
}

export const initRecaptchaVerifier = (containerId: string): RecaptchaVerifier => {
  return new RecaptchaVerifier(auth, containerId, {
    size: 'invisible',
    callback: () => {},
    'expired-callback': () => {},
  })
}

export const sendPhoneOTP = async (
  phoneNumber: string, recaptchaVerifier: RecaptchaVerifier
): Promise<ConfirmationResult> => {
  return signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier)
}

export const verifyPhoneOTP = async (
  confirmationResult: ConfirmationResult, otp: string
): Promise<UserCredential> => {
  return confirmationResult.confirm(otp)
}

export const signOutUser = async (): Promise<void> => {
  return signOut(auth)
}

export const sendPasswordReset = async (email: string): Promise<void> => {
  return sendPasswordResetEmail(auth, email)
}

export const sendVerificationEmail = async (user: User): Promise<void> => {
  return sendEmailVerification(user)
}

// ========================================
// AGC: AUTH STATE + TOKEN HELPERS
// ========================================

export const onAuthStateChange = (callback: (user: User | null) => void): (() => void) => {
  return onAuthStateChanged(auth, callback)
}

export const getCurrentUser = (): User | null => {
  return auth.currentUser
}

export const getIdToken = async (): Promise<string | null> => {
  const user = auth.currentUser
  if (!user) return null
  return user.getIdToken()
}

// AGC: v3.9.5 — robust variant that forces a single refresh when the cached token is missing.
// AGC: Sub-users (team members) sometimes hit a brief auth.currentUser=null window after long
// AGC: idle (e.g. extract-fields modal open ~30s). Use this for any request that must not 401 silently.
export const getIdTokenOrRefresh = async (): Promise<string | null> => {
  const cached = await getIdToken()
  if (cached) return cached
  const user = auth.currentUser
  if (!user) return null
  try {
    return await user.getIdToken(true)
  } catch {
    return null
  }
}
